<template>
  <v-card class="lean-embedded-documents mx-auto my-4" max-width="1000" :rounded="false" :class="{disabled}" outlined>
    <v-toolbar color="blue-grey lighten-1" dark flat dense>
      <v-toolbar-title>{{ label }}</v-toolbar-title>
      <v-spacer />
      <v-btn icon @click.native="openDialog(value ? value.length : null)" :disabled="disabled">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-container v-if="value" fluid class="px-10 py-5">
      <draggable v-if="showDraggable" v-model="value" style="min-height: 10px" handle=".handle" @change="refreshDraggable()">
        <template v-for="(record, index) in value">
          <v-list-item :key="index" class="lean-embedded-documents__list-item px-0">
            <lean-list-item-content
              @click="openDialog(index)"
              :record="getRecord(record, listConfig)"
              :listConfig="listConfig"
            >
              <template v-slot:actions>
                <div class="d-flex">
                  <v-btn v-if="!disabled" icon @click="openDialog(index)">
                    <v-icon color="grey lighten-1"> mdi-pencil </v-icon>
                  </v-btn>
                  <v-btn v-if="!disabled" icon @click="removeRecord(index)">
                    <v-icon color="grey lighten-1"> mdi-delete </v-icon>
                  </v-btn>
                  <v-icon v-if="!disabled" class="lean-embedded-documents-list-item__drag pl-4 handle" color="grey lighten-1">
                    mdi-drag-vertical
                  </v-icon>
                </div>
              </template>
            </lean-list-item-content>
          </v-list-item>
        </template>
      </draggable>
    </v-container>
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card :rounded="false">
        <v-toolbar color="blue-grey lighten-1" dark flat dense>
          <v-btn @click="dialog = false" rounded icon>
            <v-icon color="white"> mdi-close </v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn rounded icon @click="setFieldValue">
            <v-icon color="white"> mdi-content-save </v-icon>
          </v-btn>
        </v-toolbar>
        <v-container>
          <v-card-text>
            <lean-form-block
              :initialValues="dialogValue"
              :resolved="dialogResolved"
              :schema="schema"
              @change="updateDialogValues($event)"
              class="px-8"
              :key="dialogKey"
            />
          </v-card-text>
        </v-container>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import fieldMixin from '@/mixins/LeanField';
import draggable from 'vuedraggable';
import LeanListItemContent from '@/components/LeanListItemContent.vue';
import { mapState } from 'vuex';

export default {
  name: 'LeanEmbeddedDocuments',
  mixins: [fieldMixin],
  components: {
    LeanFormBlock: () => import('@/components/LeanFormBlock.vue'),
    draggable,
    LeanListItemContent
  },
  data: () => ({
    dialog: false,
    dialogValue: null,
    dialogResolved: null,
    dialogIndex: null,
    // Using an incrementing unique key forces the re-rendering of the dialog form-block component
    dialogKey: 0,
    showDraggable: true
  }),
  watch: {
    dialog(val) {
      if (!val) {
        this.dialogIndex = null;
      }
    }
  },
  methods: {
    openDialog(index) {
      this.dialogKey++;
      if (index === null) {
        this.dialogValue = {};
        this.datatypes[this.config.source].schema.map((field) => {
          this.dialogValue[field.name] = null;
        });
        // set position of new record, if it would be saved
        this.dialogIndex = 0;
        this.value = [{}];
      } else {
        this.dialogValue = Object.assign({}, this.value[index]);
        this.dialogResolved = Object.assign({}, this.resolved[index]);
        this.dialogIndex = index;
      }
      this.dialog = true;
    },
    updateDialogValues(data) {
      // TODO: what would happen if data included a newAttachment? How to pass this up the chain?
      Object.keys(data.values).map((fieldName) => {
        this.dialogValue[fieldName] = data.values[fieldName];
      });
      this.isModified = true;
    },
    setFieldValue() {
      this.value[this.dialogIndex] = Object.assign({}, this.dialogValue);
      this.dialog = false;
      // Mixin watch value does not recognize changes to array values
      this.$emit('change', this.value);
    },
    removeRecord(index) {
      this.value.splice(index, 1);
    },
    async getRecord(record, listConfig) {
      const newRecord = { ...record };
      const currentRecordConfig = this.datatypes[this.config.source];
      // check every field which is showing in the component.
      // when the field is a record/datatype field then get the value of the field from the subrecord
      Object.keys(listConfig).forEach(async (key) => {
        const fieldnames = Array.isArray(listConfig[key]) ? listConfig[key] : [listConfig[key]];

        for (const fieldname of fieldnames) {
          const recordFieldConfig = currentRecordConfig.schema.find((fieldConfig) => fieldConfig.name === fieldname);
          if (
            !!newRecord[fieldname] &&
            !!recordFieldConfig &&
            ['document', 'document[]'].includes(recordFieldConfig.type)
          ) {
            const subRecordConfig = this.datatypes[recordFieldConfig.source];
            const neededField = subRecordConfig.list[key];
            if (neededField) {
              const slugs = Array.isArray(newRecord[fieldname]) ? newRecord[fieldname] : [newRecord[fieldname]];
              // fetch subrecord from bucket
              const records = await Promise.all(
                slugs.map((slug) => {
                  return this.$store.state.sdk.getDocument(
                    this.$store.state.sdk.getDataRootPath(recordFieldConfig.source, slug, this.language) + `/index.json`
                  );
                })
              );
              newRecord[fieldname] = records.map((record) => record[neededField]);
            }
          }
        }
      });
      return newRecord;
    },
    refreshDraggable(){
      this.showDraggable= false;
      this.$nextTick(()=>this.showDraggable = true);
    }
  },
  computed: {
    ...mapState({
      datatypes: (state) => state.models
    }),
    listConfig: {
      get() {
        return this.datatypes[this.config.source].list || null;
      }
    },
    schema: {
      get() {
        return this.datatypes[this.config.source].schema;
      }
    }
  }
};
</script>
<style lang="scss">
.lean-embedded-documents:not(.disabled) .lean-list-item-content {
  cursor: pointer;
}

.lean-embedded-documents .lean-embedded-documents-list-item__drag {
  cursor: move;
}
</style>
